import React from "react";
import type { ApolloClient } from "@apollo/client";
import { Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import CircularProgressBar from "components/CircularProgressBar";
import { userConstants } from "actions/types";
import Button from "components/Button";
import Alert from "components/Alert";
import TypeUtils from "utils/typeUtils";
import { getUserState, updateRequiredDisclosures } from "actions/userActions";
import IconSpinner from "components/IconSpinner";
import "pages/signUp/individual/IndividualDisclosure.scss";
import { ScrollToFieldError } from "utils/form";
import type { RootState } from "types/state";

import * as yup from "yup";

const schema = yup.object({
  noneApply: yup.boolean().required().label("None Apply"),
  isCustodianEmployee: yup.boolean().label("Politically Exposed").required(),
});

interface DisclosureValues {
  noneApply: boolean;
  isCustodianEmployee: boolean;
}

const actions = [userConstants.UPDATE_REQUIRED_DISCLOSURES];
const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => ({
  error: errorSelector(state),
  isFetching: loadingSelector(state),
});

const mapDispatchToProps = {
  updateRequiredDisclosures,
  getUserState,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // parent-passed (not redux-injected); the original PropTypes were inert under
  // React 19, so it was never required — optional matches real usage (some
  // callers, e.g. the char test, omit it).
  userState?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  disclosure: DisclosureValues;
  // read in render() but never written via setState; preserved as optional
  loading?: boolean;
}

class CustodianDisclosure extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      disclosure: {
        noneApply: true,
        isCustodianEmployee: false,
      },
    };
  }

  submitDisclosure = (values: DisclosureValues) => {
    void this.props
      .updateRequiredDisclosures(this.props.client as ApolloClient<object>, {
        isCustodianEmployee: TypeUtils.booleanValue(values.isCustodianEmployee),
      })
      .then(() => {
        void this.props.getUserState(this.props.client as ApolloClient<object>);
      });
  };

  render() {
    if (this.state.loading) {
      return <IconSpinner centered />;
    }
    const { disclosure } = this.state;

    return (
      <div>
        <Formik
          validateOnChange={false}
          validationSchema={schema}
          onSubmit={(values) => {
            this.submitDisclosure(values);
          }}
          enableReinitialize={true}
          initialValues={{ ...disclosure }}
        >
          {({ handleSubmit, handleBlur, setFieldValue, values }) => (
            <Form noValidate onSubmit={handleSubmit} className="disclosure">
              <ScrollToFieldError />
              <div className="mega-container">
                <div
                  className="step-container is-active"
                  data-circle-percent="70"
                >
                  <section className="page-title-wrap">
                    <article className="text-cell">
                      <p className="page-title">Your Disclosures</p>
                      <p className="page-subtext">
                        We need some additional disclosures.
                      </p>
                    </article>
                    <article className="progress-cell">
                      <ul className="circular-progress-wrap">
                        <CircularProgressBar
                          strokeWidth="8"
                          sqSize="75"
                          percentage="70"
                        />
                      </ul>
                    </article>
                  </section>
                  <div id="form-employer-company">
                    <section className="form-sec-2col-disclosure">
                      <article
                        className="col-form"
                        style={{
                          paddingBottom: 0,
                        }}
                      >
                        <Row className="mb-3 control-checkbox-noneApply">
                          <Form.Check
                            type="checkbox"
                            name="noneApply"
                            checked={values.noneApply}
                            defaultChecked={true}
                            onChange={(e) => {
                              void setFieldValue("noneApply", e.target.checked);
                              if (e.target.checked) {
                                void setFieldValue(
                                  "isCustodianEmployee",
                                  false
                                );
                              }

                              window.analytics.track("Clicked None Apply");
                            }}
                          />
                          <Form.Label>
                            <b className="bold">None of these apply to me.</b>
                          </Form.Label>
                        </Row>

                        <Row
                          className="mb-3 disclosure-control-checkbox"
                          controlId="formCustodianEmployee"
                        >
                          <Form.Check
                            type="checkbox"
                            name="isCustodianEmployee"
                            checked={values.isCustodianEmployee}
                            // PRESERVED BUG (conversion-only): original JS passed a boolean to the
                            // input `value` prop, which expects string | number | readonly string[]
                            // @ts-expect-error -- boolean value preserved from original JS
                            value={values.isCustodianEmployee}
                            onBlur={handleBlur}
                            onChange={(e) => {
                              void setFieldValue(
                                "isCustodianEmployee",
                                e.target.checked
                              );
                              void setFieldValue("noneApply", false);
                              window.analytics.track(
                                "Clicked Custodian Employee"
                              );
                            }}
                          />
                          <Form.Label>
                            <b className="bold">Custodian Employee:</b> Are you
                            an employee of, or affiliated with Apex Fintech
                            Solutions?
                          </Form.Label>
                        </Row>
                      </article>
                    </section>
                    <section className="form-sec-2col">
                      <article className="col-form">
                        <div className="disclosure-submit-row">
                          {this.props.error && (
                            <Alert type="error" msg={this.props.error} />
                          )}
                          <Button
                            name="submit"
                            withArrow={true}
                            loading={this.props.isFetching}
                            disabled={
                              !(values.noneApply || values.isCustodianEmployee)
                            }
                          />
                        </div>
                      </article>
                    </section>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default connector(
  withApollo<OwnProps & PropsFromRedux>(CustodianDisclosure)
);
